import { HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CommandResult } from '@app/_models/command/commandResult';
import { DelegationDto, DelegationInformationWithSource, DelegationUserInformation } from '@app/_models/delegationDto';
import { DelegationInterceptor } from '@app/_providers/delegationinterceptor';
import { AccountService } from '@app/_services/account.service';
import { BaseTableDataService } from '@app/_services/baseTableData.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DelegationService extends BaseTableDataService<DelegationDto, string> {
  private readonly accountService = inject(AccountService);

  constructor() {
    super('delegation');
  }

  private readonly STORAGE_KEY = 'delegatorUserId';

  private readonly delegatorSource = new BehaviorSubject<string | DelegationUserInformation | undefined>(
    localStorage.getItem(this.STORAGE_KEY) ?? undefined,
  );

  private readonly delegations$ = this.accountService.currentUser$.pipe(
    switchMap((currentUser) => (currentUser ? this.getDelegationsForDelegate() : of(undefined))),
    shareReplay(1),
  );

  readonly delegators$ = this.delegations$.pipe(
    map((delegations) => {
      if (!delegations) return [];

      // Extract unique delegators from all delegation sources
      const uniqueDelegators = new Map<string, DelegationUserInformation>();

      delegations.forEach((source) => {
        source.delegations.forEach((delegation) => {
          const delegatorUserId = delegation.delegator.userId;
          if (delegatorUserId && !uniqueDelegators.has(delegatorUserId)) {
            uniqueDelegators.set(delegatorUserId, delegation.delegator);
          }
        });
      });

      return Array.from(uniqueDelegators.values());
    }),
  );

  readonly delegator$: Observable<DelegationUserInformation | undefined> = this.delegatorSource.pipe(
    switchMap((source) => {
      if (!source) {
        return of(undefined);
      }
      if (typeof source !== 'string') {
        return of(source);
      }
      return this.delegators$.pipe(map((delegators) => delegators.find((delegator) => delegator.userId === source)));
    }),
    shareReplay(1),
  );

  // GetDelegationsForDelegate - where I am the delegate (can act on behalf of others)
  getDelegationsForDelegate(): Observable<DelegationInformationWithSource[]> {
    return this.http.get<DelegationInformationWithSource[]>(`${this.baseControllerUrl}/for-delegate`, {
      context: new HttpContext().set(DelegationInterceptor.skipDelegatorInterceptor, true),
    });
  }

  // GetDelegationsForDelegator - where I am the delegator (has delegated to others)
  getDelegationsForDelegator(): Observable<DelegationInformationWithSource[]> {
    return this.http.get<DelegationInformationWithSource[]>(`${this.baseControllerUrl}/for-delegator`);
  }

  setDelegator(value: DelegationUserInformation | undefined) {
    if (value?.userId) {
      localStorage.setItem(this.STORAGE_KEY, value.userId);
      this.delegatorSource.next(value);
    } else {
      localStorage.removeItem(this.STORAGE_KEY);
      this.delegatorSource.next(undefined);
    }
  }

  validateDelegateEmail(email: string): Observable<string> {
    return this.http.get<string>(`${this.baseControllerUrl}/validate-delegate-email`, {
      params: { email },
    });
  }

  upsert(data: DelegationDto): Observable<CommandResult> {
    return this.update(data);
  }
}
